<template>
  <b-dropdown
    variant="link"
    class="methods-parameters-dropdown"
    :class="{'methods-parameters-dropdown__disabled': !selectedMethod}"
    right no-caret
  >
    <template slot="button-content">
      <i
        aria-hidden="true"
        class="fas fa-ellipsis-v text-gray"
      />
    </template>
    <template v-if="showFillLimits">
      <b-dropdown-item
        v-for="(limit, index) in limits"
        :key="index"
        @click="confirmFill(limit.type)"
      >
        <i
          :class="limit.icon"
          aria-hidden="true"
          class="mr-2 methods-parameters-dropdown__icon"
        />
        {{ $t(limit.text) }}
      </b-dropdown-item>
    </template>
    <div
      v-if="isDuvalSelected"
    >
      <b-dropdown-item
        v-for="(method, index) in duvalMethodsLookup"
        class="py-1"
        :key="`${method}-${index}`"
        @click="confirmVisibilityOnDuval(method)"
      >
        <i
          aria-hidden="true"
          class="mr-2 methods-parameters__dropdown-icon"
          :class="getVisibilityClass(method)"
        />
        <span>
          {{ getVisibilityLabel(method) }} ({{ methodName(method) }})
        </span>
      </b-dropdown-item>
    </div>
    <b-dropdown-item
      v-else-if="!isInsideTable"
      @click="confirmVisibility"
    >
      <i
        aria-hidden="true"
        class="mr-2 methods-parameters__dropdown-icon"
        :class="lotsVisibilityIcon"
      />
      <span>
        {{ lotsVisibilityMessage }}
      </span>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
import Service from '@/domain/services/installation/engineering/chromatography/parametersOfMethods';
import { Rules, TransformerApplication } from 'sigma-mappings';
import dropdownLimits from '@/domain/data/installation/engineering/chromatography/methodsParameters/dropdownLimits';

export default {
  name: 'DropdownMenu',
  props: {
    selectedMethodHasHiddenOption: {
      type: Boolean,
    },
    assetGeneralInfo: {
      type: Object,
    },
    selectedMethod: {
      type: String,
    },
    selectedNorm: {
      type: String,
    },
    assetModule: {
      type: Object,
    },
    methodsLookup: {
      type: Array,
    },
    isInsideTable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    limits: dropdownLimits,
  }),
  computed: {
    isDuvalSelected() {
      return this.selectedNorm === Rules.DUVAL;
    },
    showFillLimits() {
      return this.isInsideTable && this.selectedNorm === Rules.IEC_60599
        && this.assetGeneralInfo.transformerApplication === TransformerApplication.POTENCY;
    },
    lotsVisibilityIcon() {
      return this.selectedMethodHasHiddenOption
        ? 'fas fa-eye'
        : 'fas fa-eye-slash';
    },
    lotsVisibilityMessage() {
      return this.selectedMethodHasHiddenOption
        ? this.$t('engineering.show-lots-and-alarms')
        : this.$t('engineering.hide-lots-and-alarms');
    },
    duvalMethodsLookup() {
      let normalizedArray = [];
      const selectedMethodLookup = this.methodsLookup.find(it => it.id === this.selectedMethod);
      if (!selectedMethodLookup) {
        return this.methodsLookup;
      }
      normalizedArray = normalizedArray
        .concat(selectedMethodLookup, selectedMethodLookup.childMethods);
      return normalizedArray.map(it => it);
    },
  },
  methods: {
    getVisibility(method = {}) {
      if (method.hiddenReportsAndAlarms === undefined || method.hiddenReportsAndAlarms === null) {
        return this.selectedMethodHasHiddenOption;
      }
      return method.hiddenReportsAndAlarms;
    },
    getVisibilityMessageCode(method) {
      return this.getVisibility(method) ? 'MC011' : 'MC030';
    },
    getVisibilityLabel(method) {
      return method && method.hiddenReportsAndAlarms
        ? this.$t('engineering.show-lots-and-alarms') : this.$t('engineering.hide-lots-and-alarms');
    },
    getVisibilityClass(method) {
      return method && method.hiddenReportsAndAlarms ? 'fas fa-eye' : 'fas fa-eye-slash';
    },
    methodName(method) {
      if (!method) {
        return '';
      }
      return method.name;
    },
    confirmVisibilityOnDuval(method) {
      this.$confirmMessage({
        code: this.getVisibilityMessageCode(method),
      }).accepted(() => {
        this.handleVisibility(method);
      });
    },
    confirmVisibility() {
      this.$confirmMessage({
        code: this.getVisibilityMessageCode(),
      }).accepted(() => {
        this.handleVisibility();
      });
    },
    confirmFill(limit) {
      this.$confirmMessage({
        code: 'MC011',
      }).accepted(() => {
        this.fillWithLimits(limit);
      });
    },
    fillWithLimits(limit) {
      if (!this.selectedNorm || !this.selectedMethod) return;
      const params = {
        norm: this.selectedNorm,
        method: this.selectedMethod,
        limit,
      };
      this.$emit('update-loading', true);
      Service.getParametersTableLimited(
        this.assetModule.id,
        params,
      ).then((response) => {
        this.$emit('set-parameters', response);
        this.$emit('set-parameters-copy');
        this.$emit('call-edit-parameters', 'MS016');
      }).finally(() => {
        this.$emit('update-loading', false);
      });
    },
    handleVisibility(method = {}) {
      const visibility = this.getVisibility(method);
      const methodId = method.id;
      this.$emit('handle-visibility', visibility, methodId);
    },
  },
};
</script>

<style lang="sass" scoped>
  .methods-parameters-dropdown
    padding: 0
    margin: 0
    &__disabled
      pointer-events: none
      opacity: 0.5
    &__icon
      width: 1em
      text-align: center
</style>
